import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { lastValueFrom, Subscription } from 'rxjs';
import moment from 'moment';

import { DynamicListingComponent, FilterConfig, ColumnConfig, ActionConfig, ActionEvent, FilterEvent } from '../dynamic-listing/dynamic-listing';
import { ClientListService } from '../services/client/client-list.service';

import { ExportApiService, CreateExportJobRequest } from '../services/export/export-api.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { SVG_ICONS } from '../utils/svg-icons.utils';
import { flattenObject } from '../utils/data-flatten.utils';
import { openInNewTab } from '../utils/url.utils';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { ExportDialogComponent } from '../export-dialog/export-dialog.component';
import { ListStateService } from '../services/list-state/list-state.service';
import { MastersService } from 'libs/shared/src/lib/services/masters/masters.service';

// ClientRow is fully dynamic - all columns come from backend
type ClientRow = Record<string, any>;

@Component({
  selector: 'lib-client-list',
  standalone: true,
  imports: [CommonModule, DynamicListingComponent, DynamicDialogModule],
  providers: [DialogService],
  templateUrl: './client-list.html',
  styleUrls: ['./client-list.css'],
})
export class ClientList implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly listStateService = inject(ListStateService);
  private pageSubscription?: Subscription;

  isLoading = false;
  exportFilename = `clients_${new Date().toISOString().slice(0, 10)}`;

  tableData: ClientRow[] = [];

  fromDate: Date | null = null;
  toDate: Date | null = null;
  clientName = '';
  clientPhone = '';
  clientCategory = '';
  clientType: 'all' | 'draft' | 'non-draft' = 'all';
  referredBy = '';
  lineOfBusiness : any = '';

  lineOfBusinessOptions: any[] = [];

  filterConfigs: FilterConfig[] = [];

  // Columns are loaded dynamically from backend column_preference
  // This array will be populated by loadClients() from API response
  columnConfigs: ColumnConfig[] = [];

  actionConfigs: ActionConfig[] = [
    {
      key: 'view',
      label: 'View',
      tooltip: 'View client',
      severity: 'secondary',
      svgIcon: SVG_ICONS.VIEW,
    },
    {
      key: 'edit',
      label: 'Edit',
      tooltip: 'Edit client',
      severity: 'secondary',
      svgIcon: SVG_ICONS.EDIT,
      disabled: (row: any) => row?.approval_status?.toLowerCase() === 'approved' || row?.is_cancelled,
      disabledTooltip: 'Cannot edit client with approved or cancelled status'
    },
    {
      key: 'ledger',
      label: 'Ledger',
      tooltip: 'View client ledger',
      severity: 'secondary',
      svgIcon: SVG_ICONS.LEDGER,
    },
    {
      key: 'delete',
      label: 'Delete',
      tooltip: 'Delete client',
      severity: 'secondary',
      svgIcon: SVG_ICONS.DELETE,
      disabled: (row: any) => row?.approval_status?.toLowerCase() === 'approved' || row?.is_cancelled,
      disabledTooltip: 'Cannot delete client with approved or cancelled status'
    },
    {
      key: 'cancel',
      label: 'Cancel',
      tooltip: 'Cancel client',
      severity: 'secondary',
      svgIcon: SVG_ICONS.CROSS_ICON,
      disabled: (row: any) => row?.is_cancelled || row?.approval_status?.toLowerCase() === 'approved',
      disabledTooltip: 'Cannot cancel client with approved or already cancelled status'
    },
  ];

  pageIndex = 1;
  rowsPerPage = 10;
  totalCount = 0;
  totalPages = 0;
  columnPreferenceId?: string;

  constructor(
    private readonly router: Router,
    private readonly clientService: ClientListService,
    private readonly toasterService: ToasterService,
    private mastersService: MastersService,
    private readonly exportApiService: ExportApiService,
    private readonly dialogService: DialogService
  ) {}

  ngOnInit() {
    this.initializeFilterConfigs();
    // Subscribe to URL page changes
    this.pageSubscription = this.listStateService.getPageFromRoute(this.route).subscribe(page => {
      this.loadClients(page);
    });
  }

  ngOnDestroy() {
    this.pageSubscription?.unsubscribe();
  }

  async initializeFilterConfigs() {
    // loading options from master
    await this.loadLineOfBusiness();

    this.filterConfigs = [
      {
        key: 'fromDate',
        type: 'date',
        placeholder: 'From Date',
        value: this.fromDate,
        showInAccordion: true,
      },
      {
        key: 'toDate',
        type: 'date',
        placeholder: 'To Date',
        value: this.toDate,
        showInAccordion: true,
      },
      {
        key: 'clientName',
        type: 'text',
        placeholder: 'Client Name',
        value: this.clientName,
        showInAccordion: true,
      },
      {
        key: 'clientPhone',
        type: 'text',
        placeholder: 'Phone No.',
        value: this.clientPhone,
        showInAccordion: true,
      },
      {
        key: 'lineOfBusiness',
        type: 'select',
        placeholder: 'Line Of Business',
        options: this.lineOfBusinessOptions,
        optionLabel: 'label',
        optionValue: 'value',
        value: this.lineOfBusiness, 
        showInAccordion: true,
        showInDialog: false
      },
    ];
  }

  async loadLineOfBusiness() {
    try {
      const response = await lastValueFrom(
        this.mastersService.getMasters(['line-of-businesses'])
      ) as any;

      const mastersData = response?.data || response || {};
      const lobs = Array.isArray(mastersData['line-of-businesses'])
        ? mastersData['line-of-businesses']
        : [];

      const mapped = lobs.map((lob: any) => ({
        value: lob.id || lob.code,
        label: (lob.name || lob.line_of_business_name || `LOB ${lob.id}`)
          .toString()
          .toUpperCase()
      }));

      this.lineOfBusinessOptions.splice(
        0,
        this.lineOfBusinessOptions.length,
        ...mapped
      );

    } catch (error) {
      console.error('Error loading line of business:', error);
      this.lineOfBusinessOptions.splice(0, this.lineOfBusinessOptions.length);
    }
  }

  async loadClients(page: number = 1) {
    try {
      this.isLoading = true;
      this.pageIndex = page;

      const filters = {
        type: this.clientType,
        limit: this.rowsPerPage,
        offset: (this.pageIndex - 1) * this.rowsPerPage,
        query: this.clientName.trim() || undefined,
        phone: this.clientPhone.trim() || undefined,
        category: this.clientCategory || undefined,
        referred_by: this.referredBy.trim() || undefined,
        is_deleted: false,
        include_cancelled: 'true', // Include cancelled clients in list view (they'll be styled differently)
        from_date: this.fromDate
          ? moment(this.fromDate).format('YYYY-MM-DD')
          : undefined,
        to_date: this.toDate
          ? moment(this.toDate).format('YYYY-MM-DD')
          : undefined,
        get_references: 'true',
        line_of_business: this.lineOfBusiness || undefined,
      } as const;
      const response = await lastValueFrom(this.clientService.getClients(filters));
      const clients = response?.data?.clients ?? [];

      // Extract column preference from response
      if (response?.data?.column_preference) {
        const columnPreference = response.data.column_preference;
        this.columnPreferenceId = columnPreference.id ? String(columnPreference.id) : undefined;
        
        // Update columnConfigs with columns_list from preference
        if (columnPreference.columns_list && Array.isArray(columnPreference.columns_list)) {
          this.columnConfigs = columnPreference.columns_list.map((col: any) => ({
            key: col.key,
            header: col.header || col.label,
            type: col.type,
            ellipsis: col.ellipsis,
            align: col.align,
            is_selected: col.is_selected !== false,
          }));
        }
      }

      // Use data directly from backend - no hardcoded field mapping
      this.tableData = clients.map((client: any) => {
        const clientId = client.id;
        const flattened = flattenObject(client);
        flattened['id'] = clientId;
        return flattened;
      });

      this.totalCount = response?.data?.total ?? this.tableData.length;
      this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.rowsPerPage));
    } catch (error) {
      console.error('Error loading clients:', error);
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
      this.toasterService.error('Error', 'Failed to load clients');
    } finally {
      this.isLoading = false;
    }
  }

  onFilterEvent(event: FilterEvent) {
    this.fromDate = event.data?.fromDate ?? null;
    this.toDate = event.data?.toDate ?? null;
    this.lineOfBusiness = event.data?.lineOfBusiness ?? '';

    // Validate that from date is not after to date
    if (this.fromDate && this.toDate) {
      const from = moment(this.fromDate);
      const to = moment(this.toDate);
      if (from.isAfter(to)) {
        this.toasterService.warn('Warning', 'From Date cannot be after To Date');
        this.toDate = null;
        this.initializeFilterConfigs();
        return;
      }
    }

    this.clientName = event.data?.clientName ?? '';
    this.clientPhone = event.data?.clientPhone ? String(event.data.clientPhone) : '';
    this.clientCategory = event.data?.clientCategory ?? '';
    this.clientType = (event.data?.clientType ?? 'all') as typeof this.clientType;
    this.referredBy = event.data?.referredBy ?? '';

    // Reset to page 1 when filters change
    this.listStateService.clearPage();
    // Always reload when filters change (URL subscription may not trigger if already on page 1)
    this.loadClients(1);
  }

  onColumnsSelected(_: string[]) {
  }

  onColumnPreferenceSaved(event: { id: string; columns: ColumnConfig[] }) {
    this.columnPreferenceId = event.id ? String(event.id) : undefined;
    this.columnConfigs = event.columns;
  }

  onActionClicked(event: ActionEvent) {
    const clientId = event.row?.id;
    if (!clientId) return;

    switch (event.action) {
      case 'view':
        this.view(clientId);
        break;
      case 'edit':
        this.edit(clientId);
        break;
      case 'ledger':
        this.viewLedger(clientId);
        break;
      case 'delete':
        this.delete(clientId);
        break;
      case 'cancel':
        this.cancel(clientId);
        break;
    }
  }

  onPageChanged(page: number) {
    // Update URL - this triggers the subscription which loads the data
    this.listStateService.setPage(page);
  }

  exportCsv() {
    this.openExportDialog('csv');
  }

  exportXlsx() {
    this.openExportDialog('excel');
  }

  exportPdf() {
    window.print();
  }

  /**
   * Opens the export dialog with filter and column options.
   * @param format Default export format
   */
  private openExportDialog(format: 'excel' | 'csv'): void {
    const dialogRef = this.dialogService.open(ExportDialogComponent, {
      header: 'Export Clients',
      width: '500px',
      data: {
        module: 'client',
        columns: this.columnConfigs,
        filters: this.getCurrentFilters(),
        defaultFormat: format
      }
    });

    if (dialogRef) {
      dialogRef.onClose.subscribe((request: CreateExportJobRequest) => {
        if (request) {
          this.startExport(request);
        }
      });
    }
  }

  /**
   * Gets the current filter values for the export.
   */
  private getCurrentFilters(): Record<string, any> {
    return {
      client_name: this.clientName.trim() || undefined,
      phone: this.clientPhone.trim() || undefined,
      from_date: this.fromDate
        ? moment(this.fromDate).format('YYYY-MM-DD')
        : undefined,
      to_date: this.toDate
        ? moment(this.toDate).format('YYYY-MM-DD')
        : undefined,
      type: this.clientType !== 'all' ? this.clientType : undefined,
      line_of_business: this.lineOfBusiness || undefined,
    };
  }

  /**
   * Starts the export job via the API.
   */
  private startExport(request: CreateExportJobRequest): void {
    this.exportApiService.createExportJob(request).subscribe({
      next: () => {
        this.toasterService.success(
          'Export Started', 
          'Your export is processing. Click here to view progress →',
          5000
        );
        // Auto-open exports page in new tab after a short delay
        setTimeout(() => openInNewTab('/exports'), 1500);
      },
      error: (err: any) => {
        const message = err?.error?.message || 'Failed to start export';
        this.toasterService.error('Export Failed', message);
      }
    });
  }

view(clientId: string) {
  if (!clientId) return;

  this.mastersService.openInNewTab(
    ['/clients/onboarding'],
    { clientId, viewMode: true }
  );
}

edit(clientId: string) {
  if (!clientId) return;

  this.mastersService.openInNewTab(
    ['/clients/onboarding'],
    { clientId, viewMode: false }
  );
}

  async delete(clientId: string) {
    if (!confirm('Are you sure you want to delete this client?')) {
      return;
    }

    try {
      this.isLoading = true;
      await lastValueFrom(this.clientService.deleteClient(clientId));
      this.toasterService.success('Success', 'Client deleted successfully');
      await this.loadClients(this.pageIndex);
    } catch (error: any) {
      const message = error?.error?.message || error?.message || 'Failed to delete client';
      this.toasterService.error('Error', message);
    } finally {
      this.isLoading = false;
    }
  }

  viewLedger(clientId: string) {
    this.router.navigate(['/client-ledger', clientId]);
  }

  async cancel(clientId: string) {
    if (!confirm('Are you sure you want to cancel this client?')) {
      return;
    }

    try {
      this.isLoading = true;
      await lastValueFrom(this.clientService.cancelClient(clientId));
      this.toasterService.success('Success', 'Client cancelled successfully');
      await this.loadClients(this.pageIndex);
    } catch (error: any) {
      const message = error?.error?.message || error?.message || 'Failed to cancel client';
      this.toasterService.error('Error', message);
    } finally {
      this.isLoading = false;
    }
  }

  private formatClientType(value?: string): string {
    if (!value) {
      return '';
    }

    if (value === 'company' || value === 'individual' || value === 'partnership') {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }

    return value;
  }

  private formatApprovalStatus(value?: string): string {
    if (!value) {
      return '';
    }

    // Convert snake_case or camelCase to space-separated with proper capitalization
    return value
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}
